import { Check, LayoutGrid, Sparkles, Wallet, X, ShoppingBag } from 'lucide-react';
import { GramIcon } from '@/components/ui/GramIcon';
import { useTranslation } from 'react-i18next';

interface PurchaseSuccessModalProps {
    isOpen: boolean;
    onClose: () => void;
    itemName?: string;
    price?: number | string;
    balance?: number | string;
    /** Route + payload that opens the purchased item's own page. */
    goToPage?: string;
    goToParam?: any;
    onNavigate?: (page: string, param?: any, opts?: { refresh?: boolean }) => void;
}

export function PurchaseSuccessModal({ isOpen, onClose, itemName, price, balance, goToPage, goToParam, onNavigate }: PurchaseSuccessModalProps) {
    const { t } = useTranslation('store');
    const { t: tc } = useTranslation('common');

    if (!isOpen) return null;

    const num = (v?: number | string) => (v === undefined || v === null || v === '' ? '—' : Number(v).toFixed(2));
    const name = String(itemName || '').trim();

    const steps = [
        { icon: <Sparkles size={13} strokeWidth={2.6} />, text: t('purchaseSuccessStep1') },
        { icon: <LayoutGrid size={13} strokeWidth={2.6} />, text: t('purchaseSuccessStep2') },
        { icon: <Wallet size={13} strokeWidth={2.6} />, text: t('purchaseSuccessStep3') },
    ];

    return (
        <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/75 backdrop-blur-sm animate-in fade-in">
            <style>{`
                .psm-fade { animation: psmFade .18s ease-out }
                .psm-pop { animation: psmPop .28s cubic-bezier(.32,.72,.24,.9) }
                @keyframes psmFade { from { opacity: 0 } to { opacity: 1 } }
                @keyframes psmPop { from { transform: scale(.94) translateY(8px); opacity: .4 } to { transform: scale(1) translateY(0); opacity: 1 } }
            `}</style>

            <div className="relative glass-strong w-full sm:max-w-sm rounded-t-[32px] sm:rounded-[32px] border border-white/10 shadow-2xl p-5 psm-pop max-h-[92vh] overflow-y-auto no-scrollbar">
                {/* Success badge */}
                <div className="flex flex-col items-center text-center">
                    <div className="relative w-14 h-14 rounded-[18px] flex items-center justify-center bg-[#35E85D]/12 border border-[#35E85D]/35 shadow-[0_0_22px_rgba(53,232,93,0.18)]">
                        <Check size={26} strokeWidth={3.2} className="text-[#35E85D]" />
                    </div>
                    <h3
                        className="mt-3 text-[19px] font-black text-white leading-tight"
                        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                        {t('purchaseSuccessful')}
                    </h3>
                    <p className="mt-1 text-[12px] leading-relaxed text-white/50 font-semibold px-2">
                        {t('purchaseSuccessSubtitle')}
                    </p>
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 p-1.5 bg-white/10 hover:bg-white/20 rounded-full text-white/50 hover:text-white transition-colors"
                        aria-label={tc('close')}
                    >
                        <X size={14} strokeWidth={3} />
                    </button>
                </div>

                {/* Summary */}
                <div className="mt-4 glass-inset rounded-2xl border border-white/[0.07] divide-y divide-white/[0.06] overflow-hidden">
                    <div className="flex items-center justify-between gap-3 px-3.5 py-2.5">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-white/40">{t('purchaseSuccessItem')}</span>
                        <span className="text-[12px] font-extrabold text-white truncate portals-numeric">{name || '—'}</span>
                    </div>
                    <div className="flex items-center justify-between gap-3 px-3.5 py-2.5">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-white/40">{t('purchaseSuccessPaid')}</span>
                        <span className="flex items-center gap-1.5 text-[12px] font-extrabold text-white">
                            {num(price)}
                            <GramIcon className="w-3.5 h-3.5" />
                        </span>
                    </div>
                    <div className="flex items-center justify-between gap-3 px-3.5 py-2.5">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-white/40">{t('purchaseSuccessBalance')}</span>
                        <span className="flex items-center gap-1.5 text-[12px] font-extrabold text-[#35E85D]">
                            {num(balance)}
                            <GramIcon className="w-3.5 h-3.5" />
                        </span>
                    </div>
                </div>

                {/* Instructions */}
                <div className="mt-4">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-white/40 mb-2">{t('purchaseSuccessNext')}</p>
                    <div className="space-y-2">
                        {steps.map((s, i) => (
                            <div key={i} className="flex items-start gap-2.5">
                                <span className="shrink-0 mt-[1px] w-[22px] h-[22px] rounded-[8px] bg-[#2489FF]/15 border border-[#2489FF]/30 flex items-center justify-center text-[#2489FF]">
                                    {s.icon}
                                </span>
                                <p className="text-[12px] leading-relaxed text-white/70 font-semibold">{s.text}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Actions — single primary button that opens the user's purchases (cart) */}
                <div className="mt-5">
                    <button
                        onClick={() => { if (onNavigate) onNavigate('purchases', undefined, { refresh: true }); onClose(); }}
                        className="w-full h-[44px] rounded-2xl bg-[#2489FF] hover:bg-[#3d97ff] text-white text-[13px] font-extrabold active:scale-[0.98] transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#2489FF]/25"
                    >
                        <ShoppingBag size={15} strokeWidth={3} />
                        {t('purchasesTitle', { ns: 'marketplace', defaultValue: 'My Purchases' })}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default PurchaseSuccessModal;
